import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiCopy, FiCheck, FiShare2, FiMail } = FiIcons;

const SOCIAL = [
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    color: '#25D366',
    getUrl: (url, title) =>
      `https://wa.me/?text=${encodeURIComponent(`${title} — ${url}`)}`,
    icon: () => (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.532 5.862L.057 23.886a.5.5 0 0 0 .611.61l6.082-1.463A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.954a9.944 9.944 0 0 1-5.031-1.362l-.36-.214-3.733.899.933-3.645-.235-.374A9.944 9.944 0 0 1 2.046 12C2.046 6.507 6.507 2.046 12 2.046S21.954 6.507 21.954 12 17.493 21.954 12 21.954z" />
      </svg>
    ),
  },
  {
    id: 'email',
    label: 'Email',
    color: '#6B7280',
    getUrl: (url, title) =>
      `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${title}\n\n${url}`)}`,
    icon: () => <SafeIcon icon={FiMail} className="w-5 h-5" />,
  },
  {
    id: 'twitter',
    label: 'X / Twitter',
    color: '#000000',
    getUrl: (url, title) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    icon: () => (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.912-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    color: '#0A66C2',
    getUrl: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    icon: () => (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    id: 'facebook',
    label: 'Facebook',
    color: '#1877F2',
    getUrl: (url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    icon: () => (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
];

const ShareQR = ({ url, title, size = 'md', open, onOpenChange, hideTrigger }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [brandColor, setBrandColor] = useState('#4f46e5');
  const modalRef = useRef(null);

  const isOpen = open !== undefined ? open : internalOpen;
  const setIsOpen = (v) => {
    if (onOpenChange) onOpenChange(v);
    else setInternalOpen(v);
  };

  const thumbSize = size === 'sm' ? 40 : size === 'lg' ? 56 : 48;

  useEffect(() => {
    const c = getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-primary')
      .trim();
    if (c) setBrandColor(c);
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  const handleNativeShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch { /* user cancelled */ }
    } else {
      setShareOpen(v => !v);
    }
  }, [url, title]);

  useEffect(() => {
    if (!isOpen && !shareOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') { setIsOpen(false); setShareOpen(false); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, shareOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const qrProps = {
    value: url,
    fgColor: brandColor,
    bgColor: '#ffffff',
    level: 'M',
    includeMargin: false,
  };

  return (
    <>
      {/* Thumbnail — clickable */}
      {!hideTrigger && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          title="Show QR code"
          className="flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all hover:shadow-md focus:outline-none focus:ring-2"
          style={{
            width: thumbSize,
            height: thumbSize,
            borderColor: brandColor,
            '--tw-ring-color': brandColor,
          }}
          aria-label="Open QR code"
        >
          <QRCodeSVG {...qrProps} size={thumbSize - 4} style={{ display: 'block' }} />
        </button>
      )}

      {/* Full Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setIsOpen(false); }}
        >
          <div
            ref={modalRef}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-5 relative"
            role="dialog"
            aria-modal="true"
          >
            {/* Close */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
              aria-label="Close"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>

            {/* Title */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: brandColor }}>
                Share this page
              </p>
              <h2 className="text-lg font-bold text-gray-900 leading-snug pr-6 line-clamp-2">
                {title}
              </h2>
            </div>

            {/* Large QR */}
            <div className="flex justify-center">
              <div
                className="p-3 rounded-2xl shadow-inner"
                style={{ backgroundColor: '#f9fafb', border: `2px solid ${brandColor}22` }}
              >
                <QRCodeSVG {...qrProps} size={200} />
              </div>
            </div>

            {/* URL pill */}
            <div
              className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-mono text-gray-500 truncate"
              style={{ backgroundColor: '#f3f4f6' }}
            >
              <span className="flex-1 truncate">{url}</span>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              {/* Copy */}
              <button
                onClick={handleCopy}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 px-4 text-sm font-semibold transition-all border-2 focus:outline-none"
                style={
                  copied
                    ? { backgroundColor: '#f0fdf4', borderColor: '#22c55e', color: '#16a34a' }
                    : { backgroundColor: `${brandColor}10`, borderColor: `${brandColor}40`, color: brandColor }
                }
              >
                <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4 flex-shrink-0" />
                {copied ? 'Copied!' : 'Copy link'}
              </button>

              {/* Share */}
              <div className="relative">
                <button
                  onClick={handleNativeShare}
                  className="flex items-center justify-center gap-2 rounded-xl py-2.5 px-4 text-sm font-semibold transition-all text-white focus:outline-none border-2"
                  style={{ backgroundColor: brandColor, borderColor: brandColor }}
                  aria-expanded={shareOpen}
                >
                  <SafeIcon icon={FiShare2} className="w-4 h-4 flex-shrink-0" />
                  <span>Share</span>
                </button>

                {/* Desktop social dropdown */}
                {shareOpen && (
                  <div className="absolute right-0 bottom-full mb-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden w-44 z-10">
                    {SOCIAL.map(({ id, label, color, getUrl, icon: Icon }) => (
                      <a
                        key={id}
                        href={getUrl(url, title)}
                        target={id === 'email' ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                        onClick={() => setShareOpen(false)}
                      >
                        <span style={{ color }}><Icon /></span>
                        {label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareQR;
